import React, {useEffect, useRef, useState} from 'react';
import {convertToMiniFile, getQuickTrialSetupData, MiniFileData, uploadFile} from './quickTrial';
import {QuickTrialSideMenu} from './components/SubMenu';
import {toast, ToastContainer} from 'react-toastify';
import {
    BackArrowIcon,
    CancelIcon,
    CheckIcon,
    CloseIcon,
    CropIcon,
    DownArrowDownloadIcon,
    ExportIcon,
    ImageUploadIcon,
    MaximiseIcon,
    MinimiseIcon,
    PlusBlueIcon,
    RemoveBgIcon,
} from '../../ui-components/Iconpack';
import {Button} from '../../ui-components/Button/button';
import BgPattern from '../../assets/icons/bg-pattern.svg';
import {CommonSpinLoader} from '../../ui-components/loaders';
import {DropdownMenu, DropdownMenuContent, DropdownMenuTrigger} from '../../ui-components/Dropdown/dropdown';
import {centerAspectCrop, getImageWidthAndHeight, MediaType} from '../../utils/media';
import {DownloadFile, GetRandomPercent} from '../../utils/helper';
import {Progressbar} from '../../ui-components/ProgressBar/progressbar';
import Tracker from '../../lib/analytics/tracker';
import {ErrorTags, EventNames, EventProperty, ScreenNames} from '../../lib/analytics/events';
import {useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {TransformComponent, TransformWrapper, useControls} from 'react-zoom-pan-pinch';
import {RootState} from '../../store';
import ReactCrop, {Crop, PercentCrop} from 'react-image-crop';
import CircularProgressLoader from '../../ui-components/loaders/CircularProgressLoader';
import {ChangeLogs} from './components/ChangeLogs';

export interface TransformationObject {
    width: string;
    height: string;
    monoGrayToneApplied: boolean;
    caption: string;
    tags: Array<string>;
    format?: string;
    quality?: string;
    bgRemoved: boolean;
    paddingApplied: boolean;
    bgColorPalette?: string;
    bgImageUid?: string;
    cropProps?: {
        cropLeftPx?: number;
        cropTopPx?: number;
        cropHeightPx?: number;
        cropWidthPx?: number;
    };
    ar?: number;
}

const defaultTransformationObject: TransformationObject = {
    width: '',
    height: '',
    monoGrayToneApplied: false,
    caption: '',
    tags: [],
    bgRemoved: false,
    paddingApplied: false,
};

export const QuickTrialScreen = () => {
    const [files, setFiles] = useState<MiniFileData[]>([]);
    const [selectedFile, setSelectedFile] = useState<MiniFileData | undefined>(undefined);
    const [transformationObj, setTransformationObj] = useState<TransformationObject>(defaultTransformationObject);
    const [loading, setLoading] = useState(false);
    const [fileUploading, setFileUploading] = useState(false);
    const [filesLoading, setFilesLoading] = useState(false);
    const [imageSrc, setImageSrc] = useState('');
    const [isDragOver, setIsDragOver] = useState(false);
    const [allowUpload, setAllowUpload] = useState(true);
    const [orgUid, setOrgUid] = useState('');
    const [preselectedFileUid, setPreselectedFileUid] = useState('');
    const [zoomPercentage, setZoomPercentage] = useState(100);
    const [cropMode, setCropMode] = useState(false);
    const [intermediateCrop, setIntermediateCrop] = useState<Crop>();
    const [imgWidth, setImgWidth] = useState(0);
    const [imgHeight, setImgHeight] = useState(0);
    const [aspectRatio, setAspectRatio] = useState(0);
    const [progress, setProgress] = useState(false);

    const {fileUid} = useParams();

    const Controls = () => {
        const {zoomIn, zoomOut, resetTransform} = useControls();
        const handleZoomIn = () => {
            zoomIn();
        };
        const handleZoomOut = () => {
            zoomOut();
        };
        const handleReset = () => {
            zoomOut();
        };
        useEffect(() => {
            window.addEventListener('zoomInClicked', handleZoomIn, {once: true});
            window.addEventListener('zoomOutClicked', handleZoomOut, {once: true});
            window.addEventListener('zoomResetClicked', handleReset, {once: true});
            return () => {
                window.removeEventListener('zoomInClicked', handleZoomIn);
                window.removeEventListener('zoomOutClicked', handleZoomOut);
                window.removeEventListener('zoomResetClicked', handleReset);
            };
        }, []);
        return <></>;
    };

    const authProps = useSelector((state: RootState) => state.auth);
    const loggedIn = authProps.isAuthenticated;

    const fileName = selectedFile?.name.toLowerCase() ?? '';
    const isSvgOrGif =
        fileName.toLowerCase().endsWith(`.${MediaType.SVG}`) ||
        fileName.endsWith(`.${MediaType.SVG_XML}`) ||
        fileName.toLowerCase().endsWith(`.${MediaType.GIF}`);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const cdnUrl = selectedFile?.cdn_url;

    const toggleCropMode = () => {
        setCropMode(!cropMode);
    };

    let transformationLiterals: Array<String> = [];
    if (transformationObj.width.length > 0) {
        transformationLiterals.push('w_' + transformationObj.width);
    }
    if (transformationObj.height.length > 0) {
        transformationLiterals.push('h_' + transformationObj.height);
    }
    if (transformationObj.monoGrayToneApplied) {
        transformationLiterals.push('e_grayscale');
    }
    if (transformationObj.bgRemoved) {
        transformationLiterals.push('e_bgremoval');
    }
    if (transformationObj.bgColorPalette) {
        transformationLiterals.push('i_bgreplace,b_' + transformationObj.bgColorPalette.substring(1));
    }
    if (transformationObj.bgImageUid) {
        transformationLiterals.push('i_bgreplace,b_uid_' + transformationObj.bgImageUid);
    }
    if (transformationObj.format && transformationObj.format.trim().length > 0) {
        transformationLiterals.push('f_' + transformationObj.format);
    }
    if (transformationObj.quality && transformationObj.quality.trim().length > 0) {
        transformationLiterals.push('q_auto:' + transformationObj.quality);
    }
    if (transformationObj.cropProps) {
        transformationLiterals.push('c_crop');
        transformationLiterals.push('c_top_' + Math.floor(transformationObj.cropProps.cropTopPx!));
        transformationLiterals.push('c_left_' + Math.floor(transformationObj.cropProps.cropLeftPx!));
        transformationLiterals.push('c_w_' + Math.floor(transformationObj.cropProps.cropWidthPx!));
        transformationLiterals.push('c_h_' + Math.floor(transformationObj.cropProps.cropHeightPx!));
    }
    let modifiedCdnUrl = '';

    if (transformationLiterals.length > 0 && selectedFile?.cdn_url) {
        let modifiedUrlParts = selectedFile.cdn_url.split('/');
        const hostPart = modifiedUrlParts.slice(0, 4);
        const filePath = modifiedUrlParts.slice(4);
        modifiedCdnUrl = hostPart.join('/') + '/' + transformationLiterals.join(',') + '/' + filePath.join('/');
    }

    const commonEventProps = {
        [EventProperty.ScreenName]: ScreenNames.QUICK_TRIAL_SCREEN,
    };

    function trackError(e: any, id: string, props: Object) {
        Tracker.trackError(e, ErrorTags.QUICK_TRIAL_ERROR, {
            ...commonEventProps,
            [EventProperty.ID]: id,
            ...props,
        });
    }

    function trackEvent(id: string, props: Object) {
        Tracker.trackEvent(
            EventNames.QUICK_TRIAL_EVENT,
            {
                ...commonEventProps,
                [EventProperty.ID]: id,
            },
            {
                file_uid: selectedFile?.uid ?? 'NA',
                logged_in: loggedIn,
                ...props,
            },
        );
    }

    const updateTransformationObject = (transformationObj: TransformationObject) => {
        setTransformationObj(transformationObj);
    };

    const selectFile = (uid: string) => {
        trackEvent('ImageSelected', {uid: uid});
        const file = files.find((f) => f.uid === uid);
        if (file) {
            setSelectedFile(file);
            const caption = file?.caption ?? transformationObj.caption;
            const tags = file?.tags ?? transformationObj.tags;
            setTransformationObj({...defaultTransformationObject, caption: caption, tags: tags});
        }
    };

    const onImageDiscard = () => {
        setSelectedFile(undefined);
        setTransformationObj(defaultTransformationObject);
        setLoading(false);
        setFilesLoading(false);
        updateCtx();
    };

    const sendMessageToParent = () => {
        trackEvent('SignUpClicked', {});
        if (window && window.parent && window.innerWidth >= 768) {
            window.parent.postMessage('SignupClicked', '*');
        } else {
            window.location.href = '/signup';
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const updateCtx = () => {
        setFilesLoading(true);
        getQuickTrialSetupData()
            .then((res) => {
                const files = res.data && res.data.files ? res.data.files : [];
                setFiles(files.map((f) => convertToMiniFile(f)));
                if (!res.data?.allow_more_uploads) {
                    setAllowUpload(false);
                }
                if (res.data?.trial_org_uid) {
                    setOrgUid(res.data.trial_org_uid);
                }
            })
            .finally(() => {
                setFilesLoading(false);
            });
    };

    const uploadAndFetchFiles = (file: File) => {
        trackEvent('UploadingFile', {name: file.name});
        if (!allowUpload) {
            trackEvent('UploadLimitReached', {});
            const msg = loggedIn ? 'Go to dashboard to upload more files!' : 'Sign up to upload more files!';
            toast.error(msg);
            return;
        }
        if (file.size > 16000000) {
            trackEvent('FileTooLarge', {});
            toast.error(`File ${file.name} exceeds 16 MB. Please try with a smaller file.`);
            return;
        }
        setFileUploading(true);
        uploadFile(file)
            .then((res) => {
                if (res.file) {
                    setSelectedFile(res.file);
                } else if (res.msg) {
                    trackError('FileUploadError', res.msg, {});
                    toast.error(res.msg);
                }
            })
            .catch((err) => {
                trackError('FileUploadError', err.toString(), {});
                console.error('Error uploading file', err);
            })
            .finally(() => {
                setFileUploading(false);
                updateCtx();
            });
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            uploadAndFetchFiles(files[0]);
        }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target?.files;
        const file = files && files.length > 0 && files[0];
        if (file) {
            uploadAndFetchFiles(file);
        }
        if (e.target) {
            e.target.value = '';
        }
    };
    const handleZoomChange = (zoomScale: number) => {
        setZoomPercentage(zoomScale * 100);
    };

    const handleZoomIn = () => {
        window.dispatchEvent(new Event('zoomInClicked'));
    };

    const handleZoomOut = () => {
        window.dispatchEvent(new Event('zoomOutClicked'));
    };
    const calculateCropValue = (dimension: number, percent: number) => {
        return percent === 0 ? 0 : Math.round((dimension * percent) / 100);
    };
    const applyCrop = async (xPercent: number, yPercent: number, wPercent: number, hPercent: number) => {
        if (!selectedFile) return;
        let w: number;
        let h: number;

        if (transformationObj.width || transformationObj.height) {
            try {
                const [modifiedW, modifiedH] = await getImageWidthAndHeight(modifiedCdnUrl);
                w = modifiedW;
                h = modifiedH;
            } catch (e) {
                console.error('Error fetching image dimensions', modifiedCdnUrl, e);
                return;
            }
        } else {
            const [modifiedW, modifiedH] = await getImageWidthAndHeight(cdnUrl!);
            w = modifiedW;
            h = modifiedH;
        }

        if (w > 0 && h > 0) {
            setTransformationObj({
                ...transformationObj,
                cropProps: {
                    cropLeftPx: calculateCropValue(w, xPercent),
                    cropTopPx: calculateCropValue(h, yPercent),
                    cropHeightPx: calculateCropValue(h, hPercent),
                    cropWidthPx: calculateCropValue(w, wPercent),
                },
            });
        }
    };

    const setCropData = (c: PercentCrop) => {
        setIntermediateCrop(c);
    };

    useEffect(() => {
        if (loading) {
            setProgress(true);
        } else {
            setTimeout(() => {
                setProgress(false);
            }, 500);
        }
    }, [loading]);

    useEffect(() => {
        if (transformationObj.ar) {
            setCropMode(true);
            setAspectRatio(transformationObj.ar);
            setIntermediateCrop(centerAspectCrop(imgWidth, imgHeight, transformationObj.ar));
        }
    }, [transformationObj.ar]);

    useEffect(() => {
        if (preselectedFileUid && preselectedFileUid.length > 0 && files.length > 0) {
            selectFile(preselectedFileUid);
            setPreselectedFileUid('');
        }
    }, [files]);

    useEffect(() => {
        Tracker.init();
        if (fileUid) {
            setPreselectedFileUid(fileUid);
        }
        Tracker.setCurrentScreenName(ScreenNames.QUICK_TRIAL_SCREEN);
        Tracker.trackEvent(EventNames.SCREEN_VIEWS, {
            [EventProperty.ScreenName]: ScreenNames.QUICK_TRIAL_SCREEN,
        });
        updateCtx();
    }, []);

    useEffect(() => {
        if (selectedFile && selectedFile.cdn_url) {
            setLoading(true);

            const imageUrl = modifiedCdnUrl.length > 0 ? modifiedCdnUrl : selectedFile.cdn_url;
            const img = new Image();

            img.onload = () => {
                setImgWidth(img.width);
                setImgHeight(img.height);
                setImageSrc(imageUrl);
                setLoading(false);
            };

            img.onerror = () => {
                if (modifiedCdnUrl.length) {
                    toast.error('Oops! Something went wrong. Please try again');
                } else {
                    toast.error('Oops! something went wrong');
                }
                setLoading(false);
            };

            img.src = imageUrl;
        }
    }, [modifiedCdnUrl, selectedFile?.cdn_url]);

    const renderImage = () => {
        return (
            <div className='relative flex items-center justify-center bg-[#EAECF0]'>
                {cropMode ? (
                    <ReactCrop
                        className='max-h-[75vh] w-auto md:max-h-[75vh]'
                        aspect={cropMode ? (aspectRatio === 1 ? undefined : aspectRatio) : undefined}
                        ruleOfThirds
                        crop={intermediateCrop}
                        onChange={(c, pc) => setCropData(pc)}>
                        <img
                            src={modifiedCdnUrl.length > 0 ? imageSrc : cdnUrl}
                            alt='Loading'
                            className='max-h-[80vh] w-full'
                            style={{display: loading ? 'none' : 'block'}}
                        />
                    </ReactCrop>
                ) : (
                    <TransformWrapper
                        initialScale={1}
                        minScale={0.5}
                        maxScale={7}
                        onTransformed={(e) => handleZoomChange(e.state.scale)}>
                        <Controls />
                        <TransformComponent>
                            <img
                                src={BgPattern}
                                alt='bgpattern'
                                className='absolute top-0 h-full w-full object-cover'
                            />
                            <img
                                src={modifiedCdnUrl.length > 0 ? imageSrc : cdnUrl}
                                alt='Loading'
                                className='max-h-[80vh] w-full'
                                style={{display: loading ? 'none' : 'block'}}
                            />
                        </TransformComponent>
                    </TransformWrapper>
                )}
            </div>
        );
    };

    const RenderImageOption = (props: {imgUrl: string; uid: string}) => {
        const [isLoading, setIsLoading] = useState(true);

        const handleImageLoad = () => {
            setIsLoading(false);
        };

        return (
            <div
                className='relative flex cursor-pointer items-center justify-center'
                onClick={() => selectFile(props.uid)}>
                {isLoading && <CommonSpinLoader size={'sm'} />}
                <img
                    src={props.imgUrl}
                    alt={props.uid}
                    className='h-20 w-20 rounded-lg border-2 border-gray-200 object-cover md:h-32 md:w-32'
                    onLoad={handleImageLoad}
                    style={{display: isLoading ? 'none' : 'block'}}
                />
            </div>
        );
    };

    const renderButtons = () => {
        return (
            <div className='sticky top-0 z-10 flex min-h-[55px] justify-between border border-transparent border-b-[#333333] bg-white py-2 md:mx-2 md:bg-[#101323]'>
                <div className={'flex md:hidden'}>
                    <Button
                        variant={'nude'}
                        size={'sm'}
                        onClick={() => {
                            window.location.href = '/';
                        }}>
                        <BackArrowIcon /> Back
                    </Button>
                </div>
                <div className={'hidden md:flex'}></div>

                {/*Free trial btn on web, if not logged in*/}
                {/*{!selectedFile && !loggedIn && (*/}
                {/*    <Button*/}
                {/*        className='mx-1 hidden bg-gradient-to-r from-[#F74FAC] to-[#FCB24F] px-4 text-[14px] font-[600] text-[#FFFFFF] md:flex'*/}
                {/*        variant='primary'*/}
                {/*        size='sm'*/}
                {/*        icon*/}
                {/*        onClick={sendMessageToParent}>*/}
                {/*        <DiamondIcon />*/}
                {/*        Free Trial*/}
                {/*    </Button>*/}
                {/*)}*/}
                {selectedFile && (
                    <div className='flex items-center justify-between gap-2'>
                        {loggedIn ? (
                            <>
                                {/*mobile btn for download img*/}
                                <div className={'flex text-[#155EEF] md:hidden'}>
                                    <Button
                                        variant={'nude'}
                                        size={'sm'}
                                        icon
                                        onClick={() => {
                                            trackEvent('DownloadImageClicked', {});
                                            DownloadFile(
                                                modifiedCdnUrl.length > 0 ? modifiedCdnUrl : selectedFile?.cdn_url,
                                                selectedFile?.name,
                                            );
                                        }}>
                                        <div className={'flex gap-2'}>
                                            <DownArrowDownloadIcon /> Download Image
                                        </div>
                                    </Button>
                                </div>

                                {/*desktop btn for download img*/}
                                <div className={'hidden md:flex'}>
                                    <Button
                                        className='text-[12px] font-[600] text-white md:px-4 md:text-[14px]'
                                        variant='primary'
                                        size='sm'
                                        onClick={() => {
                                            trackEvent('DownloadImageClicked', {});
                                            DownloadFile(
                                                modifiedCdnUrl.length > 0 ? modifiedCdnUrl : selectedFile?.cdn_url,
                                                selectedFile?.name,
                                            );
                                        }}>
                                        <ExportIcon />
                                        Download Image
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <div>
                                <DropdownMenu>
                                    <DropdownMenuTrigger>
                                        {/*mobile btn for download -> signup*/}
                                        <div className={'flex text-[#155EEF] md:hidden'}>
                                            <Button variant={'nude'} size={'sm'} icon>
                                                <div className={'flex gap-2'}>
                                                    <DownArrowDownloadIcon /> Download Image
                                                </div>
                                            </Button>
                                        </div>
                                        {/*desktop btn for download -> signup*/}
                                        <div className={'hidden md:flex'}>
                                            <Button
                                                className='mx-1 px-4 text-[14px] font-[600] text-[#FFFFFF]'
                                                variant='primary'
                                                size='sm'
                                                icon>
                                                <ExportIcon />
                                                Download Image
                                            </Button>
                                        </div>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent>
                                        <div className='flex flex-col rounded-[11px] border border-[#98A2B3] px-4 py-3 hover:border-[#444CE7]'>
                                            <span className='text-[14px] font-[600] text-[#000000] md:text-[1rem]'>
                                                Download HD Image
                                            </span>
                                            <span className='text-[0.75rem] font-[400] text-[#98A2B3]'>
                                                1000 x 600 px
                                            </span>
                                        </div>

                                        <Button
                                            className='mt-[14px]'
                                            variant='primary'
                                            size='lg'
                                            onClick={sendMessageToParent}>
                                            Sign up to download
                                        </Button>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        )}
                        {/*mobile -> X btn to discard image*/}
                        <div className={'flex md:hidden'}>
                            <Button variant={'nude'} size={'sm'} icon onClick={onImageDiscard}>
                                <CloseIcon />
                            </Button>
                        </div>
                        {/*desktop -> X cancel btn to discard image*/}
                        <div className={'hidden md:flex'}>
                            <Button
                                className='text-[14px]] px-1 font-[600] text-[#262626] md:px-4'
                                variant='secondary'
                                size='sm'
                                onClick={onImageDiscard}>
                                <CloseIcon />
                                Cancel
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <div className='flex h-[100vh] bg-[#101323]'>
                {/*sub menus*/}
                <div className={'z-50 flex h-[100vh]'}>
                    <QuickTrialSideMenu
                        fileData={selectedFile}
                        transformationObject={transformationObj}
                        updateTransformationObject={updateTransformationObject}
                        orgUid={orgUid}
                        trackEvent={trackEvent}
                        trackError={trackError}
                    />
                </div>

                {/*Canvas*/}
                <div className={'w-full flex-col overflow-y-auto'}>
                    {renderButtons()}
                    <div className={'flex justify-end'}>
                        <div className={'mt-2 flex-1 md:mt-4'}>
                            {/*bg remove cta*/}
                            {!cropMode && selectedFile && !isSvgOrGif && (
                                <div className='flex w-full flex-col items-center justify-center'>
                                    <div className='flex rounded-[0.5rem] bg-[#1D2939] px-1 md:px-3 '>
                                        <div className={'flex'}>
                                            <Button
                                                className='bg-[#1D2939] hover:bg-transparent'
                                                onClick={() => {
                                                    handleZoomOut();
                                                }}>
                                                <MinimiseIcon />
                                            </Button>
                                            <span className='flex items-center justify-center text-[12px] font-[500] text-[#D0D5DD] md:text-[14px]'>
                                                {zoomPercentage.toFixed(0)}%
                                            </span>
                                            <Button
                                                className='bg-[#1D2939] hover:bg-transparent'
                                                onClick={() => {
                                                    handleZoomIn();
                                                }}>
                                                <MaximiseIcon />
                                            </Button>
                                        </div>
                                        {!isSvgOrGif && (
                                            <Button
                                                className='bg-[#1D2939] hover:bg-transparent'
                                                onClick={() => {
                                                    toggleCropMode();
                                                    setAspectRatio(1);
                                                    setIntermediateCrop(centerAspectCrop(imgWidth, imgHeight, 1));
                                                }}>
                                                <div className={'flex gap-2'}>
                                                    <CropIcon color={'#D0D5DD'} />
                                                    <span
                                                        className={
                                                            'flex items-center text-[12px] text-white md:text-[14px]'
                                                        }>
                                                        Crop
                                                    </span>
                                                </div>
                                            </Button>
                                        )}
                                        {!isSvgOrGif && (
                                            <>
                                                <div className='relative'>
                                                    <Button
                                                        variant='secondary'
                                                        size='sm'
                                                        className='relative border-transparent bg-[#1D2939] text-[12px] font-[500] text-[#D0D5DD] hover:bg-transparent md:text-[14px]'
                                                        icon
                                                        onClick={() => {
                                                            trackEvent('BgRemoveClicked', {
                                                                is_undo: transformationObj.bgRemoved,
                                                            });
                                                            setTransformationObj({
                                                                ...transformationObj,
                                                                bgRemoved: !transformationObj.bgRemoved,
                                                            });
                                                        }}>
                                                        <RemoveBgIcon />
                                                        <span className={'flex md:hidden'}>
                                                            {transformationObj.bgRemoved ? 'BG Restore' : 'Remove BG'}
                                                        </span>
                                                        <span className={'hidden md:flex'}>
                                                            {transformationObj.bgRemoved
                                                                ? 'Restore Original Background'
                                                                : 'Remove Background'}
                                                        </span>
                                                    </Button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                            {cropMode && (
                                <div className='flex justify-center gap-5'>
                                    <div
                                        className='cursor-pointer'
                                        onClick={() => {
                                            toggleCropMode();
                                            applyCrop(
                                                intermediateCrop?.x!,
                                                intermediateCrop?.y!,
                                                intermediateCrop?.width!,
                                                intermediateCrop?.height!,
                                            );
                                        }}>
                                        <CheckIcon />
                                    </div>
                                    <div
                                        className='cursor-pointer'
                                        onClick={() => {
                                            toggleCropMode();
                                            setTransformationObj({
                                                ...transformationObj,
                                                ar: undefined,
                                            });
                                        }}>
                                        <CancelIcon />
                                    </div>
                                </div>
                            )}

                            {/*uploading -> progress..... */}
                            {fileUploading && (
                                <div className='flex min-h-[250px] w-full flex-col items-center justify-center p-6 pt-3'>
                                    <span className={'my-3 text-[14px] text-white'}>Uploading...</span>
                                    <div>
                                        <Progressbar
                                            value={GetRandomPercent()}
                                            indicator='uploading'
                                            className='w-[80px] bg-[#FFF]'
                                        />
                                    </div>
                                </div>
                            )}
                            {/*image*/}
                            {(selectedFile || loading) && (
                                <div className={'flex min-h-[250px] w-full items-center justify-center p-6 pt-3'}>
                                    {progress ? (
                                        <div className={'flex text-white md:my-20'}>
                                            <CircularProgressLoader completed={!loading} />
                                        </div>
                                    ) : (
                                        renderImage()
                                    )}
                                </div>
                            )}
                            {/*Drag or upload*/}
                            {!selectedFile && !loading && !fileUploading && (
                                <div className={'my-10 flex justify-center'}>
                                    <div
                                        className={
                                            'flex flex-col items-center gap-2 rounded-lg border-[3px] border-dashed border-[#3D436A] bg-[#1B1F38] px-8 py-4 md:px-28 md:py-14'
                                        }
                                        style={{borderColor: isDragOver ? '#8f35cb' : ''}}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}>
                                        <PlusBlueIcon />
                                        <span className={'hidden text-[18px] text-white md:flex'}>
                                            Drag & Drop Here
                                        </span>
                                        <div className={'mt-10'}>
                                            <Button variant={'secondary'} onClick={handleButtonClick}>
                                                <input
                                                    id='fileUpload'
                                                    type='file'
                                                    accept='image/png, image/jpg, image/jpeg, image/webp, image/gif, image/svg, image/svg+xml'
                                                    ref={fileInputRef}
                                                    hidden
                                                    disabled={false}
                                                    onChange={handleFileInputChange}
                                                />
                                                <ImageUploadIcon />
                                                <span className={'text-inter text-[18px] font-[600] text-[#344054]'}>
                                                    Upload Image
                                                </span>
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {!selectedFile && filesLoading && files.length < 1 && (
                                <div className={'flex justify-center align-middle'}>
                                    <CommonSpinLoader size={'sm'} />
                                </div>
                            )}
                            {/*image options to choose from */}
                            {!selectedFile && files.length > 0 && (
                                <div className={'my-10 flex flex-col text-center text-white'}>
                                    <span>Ready to use images.</span>
                                    <div className={'m-5 flex justify-center overflow-x-auto'}>
                                        <div className={'flex items-center space-x-4'}>
                                            {files.map((file, index) => (
                                                <div key={index} className={'flex-shrink-0'}>
                                                    <RenderImageOption imgUrl={file.cdn_url} uid={file.uid} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        {/*Change Logs*/}
                        <div className={'hidden md:flex'}>
                            <ChangeLogs
                                originalFileType={selectedFile?.type ?? ''}
                                originalFileSize={selectedFile?.file_size ?? 0}
                                newFileType={transformationObj.format}
                                cdnUrl={modifiedCdnUrl.length > 0 ? modifiedCdnUrl : cdnUrl ?? ''}
                                bgRemoved={transformationObj.bgRemoved}
                                bgReplaced={
                                    transformationObj.bgImageUid !== undefined ||
                                    transformationObj.bgColorPalette !== undefined
                                }
                                cropped={transformationObj.cropProps !== undefined}
                                dimensionsChanged={
                                    transformationObj.height.length > 0 || transformationObj.width.length > 0
                                }
                                greyToneApplied={transformationObj.monoGrayToneApplied}
                                loading={progress}
                                className={'h-screen'}
                            />
                        </div>
                    </div>
                </div>
                <ToastContainer className={'mx-10'} position={'top-right'} pauseOnHover={true} autoClose={3000} />
            </div>
        </>
    );
};
